<template>
  <div class="min-h-screen text-white pt-2 md:pt-8">
    <div class="container max-w-screen-lg mx-auto px-4 py-8 h-full">
      <div class="flex items-center gap-4 mb-6">
        <BackToLoginButton v-if="isStandalone" />
        <h1 class="text-2xl md:text-5xl font-bold md:text-center">
          Privacy Policy
        </h1>
      </div>
      <div class="flex flex-col gap-4 pt-4 md:pt-8">
        <span>Last Modified JULY 11, 2024</span>
        <p>
          Mastermind Cognitive Training (“us,” “our,” or “we”) respects your
          privacy and the privacy of your children. This privacy policy (the
          “Privacy Policy” or “Policy”) describes the types of information we
          collect, how we use this information, and the choices that you can
          make about how we collect and use your information.
        </p>

        <p>
          Please read this Policy carefully to understand our policies and
          practices regarding your information and the information that you
          provide when you: (a) use our websites (the “Websites”) and/or mobile
          application (the “App”), (b) enroll in the Mastermind Cognitive
          Training Program (the “Program”), or (c) when you interact with us via
          email or phone, opt for financing program offered by Mastermind
          Cognitive Training’s third-party financial partner (with financing
          program, ” Services”).
        </p>
        <p>
          This Policy is part of the agreement that you may have entered with
          us, such as Services Agreement (the, “Agreement”). Any term not
          defined herein will have the same meaning as ascribed to it the
          Agreement. From time to time, we reserve the right to modify or amend
          the terms of our Policy. You will be notified of such changes by
          notifications on our Website, by e-mail notification informing you of
          such changes, or by any other reasonable means as required by
          applicable law. You should check this Policy frequently for any
          changes. Your continued use of our Website, App, Program and/or our
          Services following the posting of changes to this Policy will mean
          that you accept those changes.
        </p>

        <h3>1. What types of information do we collect?</h3>
        <h4>Information You Provide to Mastermind COGNITIVE TRAINING</h4>

        <p>
          We collect Personal Information and information that is not Personal
          Information (“Non-Personal Information,” together with Personal
          Information, “Information”). “Personal Information” means the
          information that identifies, relates to, describes, is reasonably
          capable of being associated with, or could reasonably be linked,
          directly or indirectly, to a person or a household. We may combine
          Non-Personal Information with Personal Information, and in those
          circumstances, we will treat the combined information as Personal
          Information.
        </p>
        <h4>Information Collected Automatically</h4>
        <p>
          Mastermind Cognitive Training collects information about the Program
          and Services you use and how you use them. When you visit our Website
          or download our App we automatically receive your computer’s internet
          protocol (IP) address, your operating system and browser type, the web
          pages you visit on our Website, geolocation information, and the
          address of any referring website. This information is used to operate,
          maintain, and manage our online services in order to improve our
          website.
        </p>

        <ul class="list-disc list-inside space-y-2">
          <li>
            <strong>Analytics:</strong>
            <p class="ml-4">
              Mastermind Cognitive Training uses third-party service providers,
              such as Google Analytics, which uses cookies and other similar
              technologies to collect and analyze information about how our
              website is used. You can learn more about Google Analytics at
              https://www.google.com/policies/privacy/partners/ and
              https://www.google.com/analytics/terms/us.html, and you may choose
              to opt-out by visiting https://tools.google.com/dlpage/gaoptout.
            </p>
          </li>
          <li>
            <strong>Cookies:</strong>
            <p class="ml-4">
              We may also use cookies to collect the above information. Cookies
              are small files of letters and numbers stored on your browser or
              device that enable the cookie owner to recognize the device when
              it visits websites or uses online services. The website you visit
              may set cookies directly, known as first-party cookies, or may
              trigger cookies set by other domain names, known as third-party
              cookies. While we may automatically use some cookies that are
              strictly necessary to provide the services you request or enable
              communications, we request your consent for all of our other
              cookie uses.
            </p>
            <p class="ml-4">
              We or our third-party service providers may use the following
              types of cookies:
            </p>
            <br />

            <p>
              <span class="font-bold underline">
                Analytical/performance cookies.
              </span>
              These cookies are essential for the operation of our Website and
              App. They enable you to move around the Website and use essential
              features like secure areas and login.
            </p>
            <p>
              <span class="font-bold underline">Functionality cookies.</span>
              These cookies enable helpful but non-essential Website functions
              that improve your Website experience. By recognizing you when you
              return to our Websites, they may, for example, allow us to
              personalize our content for you, greet you by name, or remember
              your preferences. These cookies generate aggregate statistics that
              are not associated with an individualized profile, and these stay
              on your computer until the duration of their use.
            </p>
            <p>
              <span class="font-bold underline">
                Advertising, tracking or targeting cookies.
              </span>
              These cookies enable different advertising related functions. They
              may allow us to record information about your visit to our
              Websites, such as pages visited, and links followed so that we can
              make our Website and the advertising displayed on it more relevant
              to your interests.
            </p>
          </li>
          <li>
            Except for strictly necessary cookies, all first-party cookies set
            by this website will expire when you block or delete these cookies.
            Third-party cookie expiration periods are set by their respective
            owners.
          </li>
        </ul>

        <h3>Information Collected From Third-Parties</h3>
        <ul class="list-disc list-inside space-y-2">
          <li>
            <strong>Third-Party Assessment Tools Providers:</strong> We may
            obtain completion reports from our third-party assessment tool
            providers (“Assessment Tool Providers”). Any report that we obtain
            from our Assessment Tool Provider is in de-identified and aggregated
            form. In some instances, you may have to establish a separate
            account. Mastermind Cognitive Training does not receive any login
            information from Assessment Tool Providers. Please read the terms
            and privacy policies of the Assessment Tool Providers carefully
            before establishing an account with them. Mastermind Sports has no
            control over how these Assessment Tools Providers collect, process,
            and transfer Information, including Personal Information.
          </li>
          <li>
            <strong>External Call Centers:</strong> We may obtain Information
            from external call centers that we have engaged to provide services
            to us.
          </li>
          <li>
            <strong>Third-Party Payment and Financing Partners:</strong> We may
            obtain your de-identified financial information from our payment
            processing partners, financing partners and other third-parties,
            such as, credit bureaus, and identity verification services.
          </li>
          <li>
            <strong>Social Media:</strong> You may choose to provide us with
            access to certain Personal Information stored by third parties such
            as social media sites (such as Facebook and Twitter). The
            information we have access to varies by site and is controlled by
            your privacy settings on that social media site and your
            authorization. By associating an account managed by a third party
            with your Mastermind Cognitive Training account and authorizing
            Mastermind Cognitive Training to have access to this information,
            you agree that Mastermind Cognitive Training may collect, store and
            use this information in accordance with this Privacy Policy.
          </li>
        </ul>

        <h3>2. How do we use this information?</h3>

        <span>
          Mastermind Cognitive Training may use your Information in the
          following ways:
        </span>
        <h4>To Provide and Improve Our Programs</h4>
        <p>
          Mastermind Cognitive Training may use your information to aid
          enrollment or registration, prepare and administer a personalized plan
          for your child, facilitate any events, services or resources offered,
          and to process your payment. We also use your information to improve
          your experience while visiting our Website and App, and accessing our
          Programs and Services.
        </p>
        <p>
          With your permission and applicable releases, Mastermind Cognitive
          Training may use information relating to your experience with the
          Program. This information may be used in marketing, such as the
          testimonials featured on our website. We will ask your permission
          before using your information for this marketing or outreach purpose
          and you may choose to opt-out of this use of your information at any
          time. To opt-out, please see Section 6 below titled Access and Control
          of Your Personal Information.
        </p>
        <h4>To Send Transactional Information</h4>
        <span>
          Mastermind Cognitive Training uses your Information to send you
          invoices and other transaction related information.
        </span>

        <h4>To Communicate With You</h4>
        <p>
          Mastermind Cognitive Training uses your Information to respond to you
          via email, phone call, text, social media platforms (including chat
          message), Google applications, and other forms of communication when
          you contact us and to keep you informed when you inquire, assess or
          enroll. We also use your information to communicate with you via
          email, phone call, text, social media platforms (including chat
          message), Google applications, and other forms of communication about
          our products, services, resources, and marketing that we believe may
          interest you.
        </p>

        <p>
          Mastermind Cognitive Training is committed to providing you with the
          choice not to receive marketing information. To opt-out, please see
          Section 6 below titled Access and Control of Your Personal
          Information.
        </p>

        <h4>Research Purposes</h4>
        <p>
          Mastermind Cognitive Training may use de-identified or anonymized
          Information for scientific, historical, or statistical research in the
          public interest that adheres to all other applicable ethics and
          privacy laws.
        </p>

        <h3>3. How is this information shared?</h3>
        <h4>Third Parties</h4>

        <p>
          If it is necessary, Mastermind Cognitive Training may share your
          Personal Information with third parties to perform services on our
          behalf such as, providing technical services, communicating with you,
          sending emails, processing your credit card, providing Programs or
          Services, or sending marketing emails. These third parties may only
          use the personal information that is necessary to perform the function
          required and may not use it for any other purpose.
        </p>

        <h3>To Comply With Legal Obligations</h3>
        <p>
          We may access, preserve, use, or disclose your Information if we have
          a good faith belief that the law requires us to do so, or that it is
          reasonably necessary to detect, prevent, and address security,
          technical issues, or fraud.
        </p>

        <h3>Business Use</h3>
        <p>
          We may access, preserve, use, or disclose your Information in order to
          protect the rights of the business, its property, its employees and
          others, or to enforce Mastermind Cognitive Training’s billing or
          contracts.
        </p>
        <p>
          In the unlikely event that Mastermind Cognitive Training is involved
          in a merger, acquisition, sale or other change in ownership, our
          customers’ information may be among the transferred business assets.
        </p>

        <h4>Research Purposes</h4>
        <p>
          Mastermind Cognitive Training may share de-identified or anonymized
          Information with third-party researchers for scientific, historical,
          or statistical research in the public interest that adheres to all
          other applicable ethics and privacy laws
        </p>

        <h3>4. Children’s Privacy</h3>
        <p>
          Mastermind Cognitive Training is committed to protecting children’s
          privacy and safety on the Internet. We never intentionally collect or
          maintain any Personal Information about anyone under the age of 13,
          other than as needed to enroll your child, and to obtain appropriate
          information to plan and assess your child’s progress in the program.
          We will obtain verifiable parental consent before knowingly collecting
          Personally Information from anyone under the age of 13.
        </p>

        <h3>5. Opt-Out</h3>
        <h4>Notifications</h4>
        <p>
          If you no longer wish to receive notifications about our Programs or
          Services, you may click the unsubscribe button, which is in the footer
          of our emails. Alternatively, you may be able to indicate your
          preference by logging into your account and adjusting your preferences
          or by following the directions provided with the communication. You
          will not be able to log out of “transactional” emails.
        </p>

        <h4>SMS Messaging</h4>
        <p>
          When you complete a form on our website or request more information,
          you agree to receive SMS messages. You may opt out of receiving SMS
          messages by replying STOP at any time or by adjusting your settings in
          your account through Mastermind Cognitive Training's portal. By opting
          not to receive SMS messages, your enjoyment of the Program may be
          impacted.
        </p>

        <h4>Access Your Information</h4>
        <p>
          You can review and edit your personal information at any time by
          logging in to your account or by contacting us at
          info@mastermindsports.com.
        </p>

        <h3>6. California Do Not Track Disclosure</h3>
        <p>
          Do Not Track is a privacy preference that can be set in a web browser.
          At this time we do not respond to Do Not Track browser settings or
          signals.
        </p>

        <h3>7. California Shine The Light Law</h3>
        <p>
          Mastermind Cognitive Training does not disclose or sell personally
          identifiable information to third parties for direct marketing
          purposes. Should this practice change, we will update our Policy and
          provide you with appropriate notice.
        </p>

        <h3>8. Security</h3>
        <p>
          Mastermind Cognitive Training cares about the security of your
          Personal Information. The Information you submit is encrypted and
          stored with reputable third parties (vendors include but are not
          limited to HubSpot, Amazon Web Services, and Google) to reduce the
          risk of unauthorized access. While no transmission over the Internet
          or method of electronic data storage is 100% secure, we take
          reasonable precautions to prevent unauthorized access, use, or
          disclosure of your information. Our vendors also use appropriate
          administrative, technical, organizational, and physical security
          measures to protect our users’ and visitors’ information and data
          against accidental or unlawful destruction, loss, and alteration, and
          against unauthorized disclosure and access. They use standard industry
          practices to protect visitor and user information, including
          firewalls, SSL encryption, limiting storage of financial information
          to a PCI compliant third party provider, system redundancies, and
          co-location at a 24/7 secured, controlled environment.
        </p>

        <h3>9. Changes To Our Privacy Policy</h3>
        <p>
          From time to time it may become necessary to update or change our
          Privacy Policy. Mastermind Cognitive Training encourages you to
          periodically check here for the most current version of our Privacy
          Policy. If significant changes are made, Mastermind Cognitive Training
          will provide you with appropriate notice, which may include email
          notification.
        </p>

        <h3>10. Additional Requests And Contact Information</h3>
        <p>
          For questions about our privacy policy, to make choices about
          receiving marketing or promotional communications, or if you have a
          request to access, correct or delete any Personal Information, please
          contact info@mastermindsports.com.
        </p>

        <h3>Children's Privacy Policy</h3>
        <p>
          Mastermind Cognitive Training (“we” or “us”) is committed to
          protecting the privacy of children.
        </p>

        <h4>What Information We Collect</h4>
        <p>
          We require account holders to be 18 years of age or older and all
          users to be 13 years of age or older. If we determine that a child
          under 18 created a primary account, we will immediately suspend access
          to the account and delete any personal information associated with the
          child.
        </p>
        <p>
          We only collect as much information about a child as is reasonably
          necessary for the child to use our services, and we do not condition
          the child’s participation on the disclosure of more personal
          information than is reasonably necessary.
        </p>

        <h4>Information We Collect Directly</h4>
        <p>
          We may collect the following information from children: name, age,
          sex, email address, and a unique username the child creates for their
          profile. We will also be tracking the time spent within applications
          and the results from training.
        </p>

        <h4>Automatic Information Collection</h4>
        <p>
          In addition, we use certain technologies, such as cookies, to
          automatically collect information from our users (including children)
          when they visit or use our services. The information we collect
          through these technologies may include:
        </p>

        <ul class="list-disc list-inside space-y-2">
          <li>
            <strong>Analytics:</strong> Mastermind Cognitive Training uses
            third-party service providers, such as Google Analytics, which uses
            cookies and other similar technologies to collect and analyze
            information about how our website is used. You can learn more about
            Google Analytics at
            https://www.google.com/policies/privacy/partners/ and
            https://www.google.com/analytics/terms/us.html, and you may choose
            to opt-out by visiting https://tools.google.com/dlpage/gaoptout.
          </li>
          <li>
            <strong>Cookies:</strong> We may also use cookies to collect the
            above information. Cookies are small files of letters and numbers
            stored on your browser or device that enable the cookie owner to
            recognize the device when it visits websites or uses online
            services. The website you visit may set cookies directly, known as
            first-party cookies, or may trigger cookies set by other domain
            names, known as third-party cookies. While we may automatically use
            some cookies that are strictly necessary to provide the services you
            request or enable communications, we request your consent for all of
            our other cookie uses. We or our third-party service providers may
            use the following types of cookies:

            <p class="mt-2">
              <span class="font-bold underline">
                Analytical/performance cookies.
              </span>
              These cookies allow us and our third-party service providers to
              recognize and count the number of users and to see how users move
              around our Websites when they are using it. This helps us improve
              how our Websites work by, for example, ensuring that users can
              easily find what they need on our Websites. These cookies generate
              aggregate statistics that are not associated with an
              individualized profile, and these stay on your computer until you
              and these stay on your computer until you delete them.
            </p>

            <p class="mt-2">
              <span class="font-bold underline">Functionality cookies.</span>
              These cookies enable helpful but non-essential Website functions
              that improve your Website experience. By recognizing you when you
              return to our Websites, they may, for example, allow us to
              personalize our content for you, greet you by name, or remember
              your preferences. These cookies generate aggregate statistics that
              are not associated with an individualized profile, and these stay
              on your computer until the duration of their use. the duration of
              their use.
            </p>

            <p class="mt-2">
              <span class="font-bold underline">
                Advertising, tracking or targeting cookies.
              </span>
              These cookies enable different advertising related functions. They
              may allow us to record information about your visit to our
              Websites, such as pages visited, and links followed so that we can
              make our Website and the advertising displayed on it more relevant
              to your interests.
            </p>
          </li>
          <li>
            Except for strictly necessary cookies, all first-party cookies set
            by this website will expire when you block or delete these cookies.
          </li>
        </ul>
        <p>
          If you have questions about opting out of certain information
          collection, email us at support@mastermindtraining.com.
        </p>

        <h4>How Personal Information is Used</h4>
        <p>
          We use the personal information we collect from you about your child,
          or that we collect directly from your child once you set up their
          account, to register the child to use our services, communicate with
          the child about activities of features of our services that may be of
          interest, track the child’s performance through use of the services,
          [and [describe other]].
        </p>
        <p>
          We use the information we collect automatically through technology and
          other non-personal information we collect to improve our services and
          to deliver a better and more personalized experience by enabling us to
          track usage patterns for our services, store information about the
          child’s preferences which allows us to customize the content according
          to individual interests, and speed up searches.
        </p>
        <p>
          We will use the email address to send reminders, tips, and other
          messaging related directly to their training program to all
          participants. We will distribute marketing and promotional materials
          via email, but those materials will only be distributed to individuals
          who are at least 18 years old.
        </p>
        <p>
          The username for each child may be displayed on leaderboards within
          the game. No other personally identifiable information will be
          displayed, and other users will not be able to access any additional
          information through any of our systems. The username is a unique
          identification for gameplay purposes only created by each user, and
          does not require any portion of the username to contain personal
          information. There is no chat or interaction feature between users to
          ensure protection of children, and we will only send surveys to
          primary accounts, not to a child’s account.
        </p>

        <h4>Our Practices for Sharing Children’s Information</h4>
        <p>
          Our web services and database partners will be responsible for data
          storage, and both organizations have strict policies and enforcement
          around limiting access to personally identifiable information. They
          are allowed to use information we share with them only for the limited
          business purposes for which we have engaged them. Neither organization
          is allowed to distribute or sell any customer information that is
          stored in either system. Our web services partner is Alternova and
          they can be reached at 500 Beale Street, Unit 310, San Francisco, CA,
          94105; +57 3122957389; or info@alternova.co.
        </p>
        <p>
          Please direct inquiries about Alternova’s privacy practices and use of
          children’s information through the contact information provided above.
        </p>
        <p>
          We also have the right to disclose a child’s personal information
          where we are required to do so by law or legal process, such as to
          comply with any court order or subpoena, or if we believe disclosure
          is necessary or appropriate to protect the rights, property, or safety
          of Mastermind Cognitive Training and its related parties, including to
          protect the safety of a child, protect the security of our services,
          or enable us to take precautions against liability.
        </p>
        <p>
          If we are involved in a merger, acquisition, restructuring,
          reorganization, dissolution, or other sale of transfer of some or all
          of our assets, we may transfer the personal information we have
          collected to the buyer or other successor.
        </p>

        <h4>Parent/Guardian Rights</h4>

        <p>
          We want to ensure parents/guardians are familiar with their rights
          related to their child(ren):
        </p>
        <ul class="list-disc list-inside space-y-2">
          <li>
            The company will limit collection of information to only what is
            necessary to measure outcomes of the program.
          </li>
          <li>
            Parents/guardians have the right to review their child’s personal
            information, direct us to delete it, or request that we stop
            collecting any additional information.
          </li>
          <li>
            Parents/guardians have the right to agree to the collection of
            information, but can request that it not be shared with third
            parties.
          </li>
        </ul>

        <p>
          If a parent/guardian would like to communicate with the company about
          exercising their rights, they should send an email with their request
          to support@mastermindtraining.com.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import BackToLoginButton from "@/components/general/BackToLoginButton.vue";
import { computed } from "vue";
import { useRoute } from "vue-router";

const route = useRoute();

const isStandalone = computed(() => {
  return route.meta.isStandalone;
});
</script>

<style scoped lang="postcss">
h3 {
  @apply text-2xl font-bold;
}

h4 {
  @apply text-xl font-bold;
}
</style>
